import { forwardRef, memo, useCallback, useState, useMemo } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import Autocomplete from '@shared/components/autocomplete/Autocomplete';
import { TextField } from '@shared/views/form-dialog';
import EffectIcon from '@resources/icons/EffectIcon';
import themeColor from 'theme';

const useStyles = makeStyles((theme) => ({
  textFieldRoot: {
    height: '42px',
    width: '200px',
    "& > div.MuiAutocomplete-inputRoot[class*='MuiOutlinedInput-root']": {
      height: '42px',
      padding: '0 35px 0 12px !important',
      '& > input': {
        height: '42px',
        padding: '9.5px 4px 9.5px 25px',
      },
      '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
        fill: '#192A3E',
      },
      '& .MuiOutlinedInput-notchedOutline': {
        borderColor: '#C3D0E1',
      },
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderWidth: '1px',
    },
  },
  valueSelected: {
    '& > div > .MuiOutlinedInput-notchedOutline': {
      borderColor: `${theme.palette.primary.main} !important`,
    },
    '& > div > input': {
      color: theme.palette.primary.main,
    },
    '& .MuiAutocomplete-endAdornment .MuiSvgIcon-root': {
      fill: `${theme.palette.primary.main} !important`,
    },
  },
  placeholder: {
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    color: '#192A3E',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: 500,
    '& svg': {
      marginRight: '8px',
    },
  },
}));

const TYPE_LIST = [
  { name: 'Left-to-right arc' },
  { name: 'Right-to-left arc' },
  { name: 'Right turn with panning' },
  { name: 'Left turn with panning' },
  { name: 'Pull out with left turn' },
  { name: 'Pull out with right turn' },
];

const VideoViewTextField = forwardRef(({
  name,
  label,
  onChange,
  value,
}, ref) => {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  const currentValue = useMemo(() => {
    if (!value) return null;
    return TYPE_LIST.find(opt => opt.name === value) || null;
  }, [value]);

  const handleChange = useCallback((_, data) => {
    if (data && data.name) {
      onChange(data.name);
    } else if (typeof data === 'string') {
      const option = TYPE_LIST.find(op => op.name === data);
      if (option) {
        onChange(option.name);
      } else {
        onChange('');
      }
    } else {
      onChange('');
    }
  }, [onChange]);

  const renderInput = useCallback((params) => {
    const showPlaceholder = !value && !params.inputProps.value;
    const iconColor = value ? themeColor.palette.primary.main : '#192A3E';

    return (
      <TextField
        { ...params }
        name={ name }
        classes={ {
          root: classNames(classes.textFieldRoot, {
            [classes.valueSelected]: !!value,
          }),
        } }
        variant="outlined"
        InputProps={ {
          ...params.InputProps,
          startAdornment: showPlaceholder ? (
            <div
              className={ classes.placeholder }
              onClick={ () => setOpen(true) }
            >
               <EffectIcon fill={ iconColor } />
              Camera Effects
            </div>
          ) : (
            <div className={ classes.placeholder }>
               <EffectIcon fill={ iconColor } />
            </div>
          ),
        } }
        placeholder={ showPlaceholder ? label : undefined }
      />
    );
  }, [classes, label, name, ref, value]);

  return (
    <Autocomplete
      options={ TYPE_LIST }
      disableClearable
      open={ open }
      onOpen={ () => setOpen(true) }
      onClose={ () => setOpen(false) }
      getOptionLabel={ (option) => option.name }
      isOptionEqualToValue={ (option, val) => option.name === (typeof val === 'string' ? val : val?.name) }
      onChange={ handleChange }
      value={ currentValue }
      renderInput={ renderInput }
    />
  );
});

export default memo(VideoViewTextField);
